/**
 * The last organisation and campaign entered, per device and per Firebase uid
 * (platform design §17.8). A convenience: a missing, unreadable or refused
 * value falls back to the default landing, never to an error.
 */

import { z } from 'zod';

export interface LastUsed {
  tenantId: string;
  campaignId: string | null;
}

const lastUsedSchema = z.object({ tenantId: z.string(), campaignId: z.string().nullable() });

const keyFor = (uid: string): string => `socialsrc.lastUsed.${uid}`;

export function readLastUsed(uid: string): LastUsed | null {
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (raw === null) return null;
    const parsed = lastUsedSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch (error) {
    // Storage disabled by the browser, or a value that is not JSON.
    if (error instanceof DOMException || error instanceof SyntaxError) return null;
    throw error;
  }
}

export function writeLastUsed(uid: string, entry: LastUsed): void {
  try {
    localStorage.setItem(keyFor(uid), JSON.stringify(entry));
  } catch (error) {
    // Quota exceeded or storage disabled: the default landing still works.
    if (!(error instanceof DOMException)) throw error;
  }
}
